// COMMON TRADE PANEL popups (redesign stage 1): Settings · Analyse · Trade Details · Show Details. One frame with
// section tabs on the left; every section is an EXISTING editor, analysis or detail view of the trade's own module.
// Nothing new is computed except model Greeks (Black-76, r = 0, labelled) and a P&L table read off the same payoff.
// A section GroundZero does not have says so; nothing is invented.
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { px } from "@/components/options/OptionsCommon";
import { rupees } from "@/components/options/PairValuation";
import ExitSettings, { SELL_EXIT_FIELDS } from "@/components/options/TradeExitSettings";
import { RulesEditor as IcRulesEditor } from "@/components/iron_condor/IcTrades";
import { MinNetEditor, OrdersAudit, RulesDialogBody } from "@/components/iron_condor/BoxTrades";
import BoxAnalysis from "@/components/iron_condor/BoxAnalysis";
import { CardDialog, KV } from "@/components/trade/TradeCardShell";
import { condorLegs, FuturesSpreadAnalysis, IronCondorAnalysis, OptionsHedgeAnalysis, optionLegs } from "@/components/trade/TradeAnalysis";
import { futuresSpreadScenario, optionExpiryPayoff, payoffAt } from "@/lib/payoff";
import { greeks, impliedVol, ivPrice, yearsToExpiry } from "@/lib/iv";
import { BASIS, STRATEGY } from "@/lib/commonTrades";

export const POPUPS = { settings: "Settings", analyse: "Analyse", details: "Trade Details", show: "Show Details" };
const NA = ({ children }) => <div className="text-xs text-[var(--term-muted)] rounded-sm border border-dashed border-[var(--term-border-strong)] p-3" data-testid="popup-na">{children}</div>;
const signed = (v) => (v == null ? "—" : `${v > 0 ? "+" : v < 0 ? "−" : ""}₹${Math.abs(Number(v)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const tone = (v) => (v == null || Number(v) === 0 ? "" : v > 0 ? "text-[#00C805]" : "text-[#FF3B30]");
const when = (v) => { if (!v) return "—"; const d = typeof v === "number" ? new Date(v * 1000) : new Date(v); return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false }); };

/** The trade's own detail (+ the Options P&L view), loaded when the popup opens. Futures rows already carry it. */
function useDetail(row) {
  const [d, setD] = useState(row.category === "FUTURES" ? { trade: row.futures } : null);
  const [err, setErr] = useState(null);
  const reload = () => {
    if (row.category === "FUTURES") return;
    const base = { OPTIONS: "/options-hedge/trades", IRON_CONDOR: "/iron-condor/trades", BOX: "/box-spread/trades" }[row.category];
    Promise.all([api.get(`${base}/${row.id}`), row.category === "OPTIONS" ? api.get(`${base}/${row.id}/pnl`) : Promise.resolve(null)])
      .then(([a, b]) => { setD({ trade: a.data, pnl: b?.data || null }); setErr(null); })
      .catch((e) => setErr(apiError(e)));
  };
  useEffect(reload, [row.key]);  // eslint-disable-line react-hooks/exhaustive-deps
  return { d, err, reload };
}

function Tabs({ tabs, active, onPick, children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[170px_minmax(0,1fr)] gap-3 min-h-[320px]">
      <nav className="flex sm:flex-col gap-0.5 overflow-x-auto sm:border-r border-[var(--term-border)] sm:pr-2" role="tablist">
        {tabs.map(([k, l]) => <button key={k} role="tab" aria-selected={active === k} onClick={() => onPick(k)} data-testid={`popup-tab-${k}`}
          className={`text-left text-xs px-2.5 py-1.5 rounded-sm whitespace-nowrap ${active === k ? "bg-[#2563EB]/12 text-[#2563EB] font-bold" : "text-[var(--term-secondary)] hover:bg-[var(--term-hover)]"}`}>{l}</button>)}
      </nav>
      <div className="min-w-0 space-y-2">{children}</div>
    </div>
  );
}

// ───────────────────────── A. SETTINGS ─────────────────────────
function SettingsBody({ row, d, reload, onChanged, onFuturesSettings }) {
  const [tab, setTab] = useState("trade");
  const t = d.trade;
  const save = (body) => api.put(`/options-hedge/trades/${row.id}/settings`, body)
    .then(() => { toast.success("Trade settings saved"); reload(); onChanged(); })
    .catch((e) => toast.error(apiError(e)));
  const tabs = [["trade", "Trade & Risk Settings"], ["auto", "Auto Square-off"], ["alerts", "Alert Settings"], ["display", "Display Settings"]];
  return (
    <Tabs tabs={tabs} active={tab} onPick={setTab}>
      {tab === "trade" && <>
        {row.finished && <NA>This trade is finished: its settings can no longer change.</NA>}
        {!row.finished && row.category === "OPTIONS" && (t.exit_in_progress
          ? <NA>A square-off is in progress: settings are locked until it finishes.</NA>
          : <ExitSettings t={t} fields={row.strategy === "SELL_OPTIONS" ? SELL_EXIT_FIELDS : undefined} onSave={save}
            note={row.strategy === "SELL_OPTIONS" ? `Target % is of Allot Funds (₹${px(t.allocated_fund)}). Target / Stop loss compare the SELL P&L at the ask, on live prices only.` : undefined} />)}
        {!row.finished && row.category === "IRON_CONDOR" && (["CLOSED", "EXITING"].includes(t.state)
          ? <NA>Exiting: rules are locked.</NA> : <IcRulesEditor trade={t} onSaved={() => { reload(); onChanged(); }} />)}
        {!row.finished && row.category === "BOX" && <>
          <RulesDialogBody t={t} reload={() => { reload(); onChanged(); }} />
          {t.state === "PLANNED" && <MinNetEditor trade={t} onSaved={() => { reload(); onChanged(); }} />}
        </>}
        {!row.finished && row.category === "FUTURES" && <div className="space-y-2 text-xs">
          <div className="text-[var(--term-secondary)]">Target, ladder, funding and execution settings of this futures trade open in its own editor
            (the one the Futures card uses; executed levels stay frozen).</div>
          <button className="h-8 px-3 rounded-sm bg-[#2563EB] text-white font-bold" onClick={onFuturesSettings} data-testid="popup-futures-settings">Open trade settings</button>
        </div>}
      </>}
      {tab === "auto" && <div className="space-y-2 text-xs">
        <KV label="Auto square-off" value={row.auto?.on ? "ON" : "OFF"} cls={row.auto?.on ? "text-[#00C805]" : ""} />
        {row.auto?.kind === "RULES" && <div className="text-[var(--term-secondary)]">This strategy exits automatically from its rules (target, stop, expiry window) - edit them under Trade &amp; Risk Settings.</div>}
        {row.auto?.kind !== "RULES" && <div className="text-[var(--term-secondary)]">Switch it with the toggle in the panel's Auto Square Off column (the same control as the trade card).</div>}
      </div>}
      {tab === "alerts" && <NA>Alert settings are not available in GroundZero yet. Warnings show on the trade row and in the header.</NA>}
      {tab === "display" && <NA>Per-trade display settings are not available yet. Theme and detail level are under Settings.</NA>}
    </Tabs>
  );
}

// ───────────────────────── B. ANALYSE ─────────────────────────
function legsOf(row, d) {
  if (row.category === "OPTIONS") return d.pnl ? optionLegs(d.trade, d.pnl, row.strategy === "SELL_OPTIONS") : [];
  if (row.category === "IRON_CONDOR") return condorLegs(d.trade);
  return [];
}

function PnlTable({ row, d }) {
  const spot = row.spot;
  const rows = useMemo(() => {
    if (row.category === "FUTURES") {
      const sc = futuresSpreadScenario(d.trade.levels, d.trade.current_spread);
      if (!sc || sc.unbalanced) return null;
      const steps = [-1, -0.5, -0.25, 0, 0.25, 0.5, 1].map((f) => sc.avgEntry + f * Math.max(Math.abs(sc.avgEntry), 1));
      return { head: "Spread at exit", basis: "gross, before charges", rows: steps.map((x) => [x, Math.round((sc.qty * (sc.avgEntry - x)) * 100) / 100]) };
    }
    const legs = legsOf(row, d);
    const p = optionExpiryPayoff(legs, { spot });
    if (!p) return null;
    const ref = spot || (p.strikes[0] + p.strikes[p.strikes.length - 1]) / 2;
    const pts = [-0.1, -0.05, -0.02, 0, 0.02, 0.05, 0.1].map((f) => ref * (1 + f));
    return { head: "Underlying at expiry", basis: "theoretical expiry payoff · gross, before charges", rows: pts.map((x) => [x, Math.round(payoffAt(legs, x) * 100) / 100]) };
  }, [row, d, spot]);
  if (!rows) return <NA>No confirmed fills to tabulate yet.</NA>;
  return (
    <div className="space-y-1" data-testid="popup-pnl-table">
      <div className="text-[10px] text-[var(--term-muted)]">{rows.basis}{spot && row.category !== "FUTURES" ? ` · rows at −10% … +10% of spot ${px(spot)}` : ""}</div>
      <table className="mono text-xs w-full max-w-md"><thead className="text-[var(--term-muted)]"><tr><th className="text-left">{rows.head}</th><th className="text-right">P&amp;L</th></tr></thead>
        <tbody>{rows.rows.map(([x, v], i) => <tr key={i} className="border-t border-[var(--term-border)]"><td>{px(x)}</td><td className={`text-right ${tone(v)}`}>{signed(v)}</td></tr>)}</tbody></table>
    </div>
  );
}

function GreeksTable({ row, d }) {
  const legs = legsOf(row, d);
  const T = yearsToExpiry(d.trade.expiry, row.market);
  const F = row.spot;
  const quotes = d.pnl?.prices?.legs || {};
  const out = legs.map((l) => {
    const q = l.quoteKey ? quotes[l.quoteKey] : quotes[l.token] || quotes[String(l.token)];
    const mid = ivPrice(q);
    const iv = mid && F && T ? impliedVol(l.type === "CE", mid, F, Number(l.strike), T) : null;
    const g = iv ? greeks(l.type === "CE", F, Number(l.strike), T, iv) : null;
    const k = (l.side === "BUY" ? 1 : -1) * l.units;
    return { l, iv, g: g && { delta: g.delta * k, gamma: g.gamma * k, vega: g.vega * k, theta: g.theta * k } };
  });
  if (!legs.length || !F || !T) return <NA>Greeks unavailable: they need a held option leg, a current underlying price and time to expiry.</NA>;
  const tot = (f) => out.reduce((a, x) => (x.g ? a + x.g[f] : a), 0);
  const f2 = (v) => (v == null ? "—" : Number(v).toFixed(2));
  return (
    <div className="space-y-1" data-testid="popup-greeks">
      <div className="text-[10px] text-[var(--term-muted)]">MODEL Greeks: Black-76, r = 0, IV from each leg's live bid/ask mid (else LTP), position-weighted (units held). Display only.</div>
      <div className="overflow-x-auto"><table className="mono text-xs w-full min-w-[520px]"><thead className="text-[var(--term-muted)]"><tr>
        <th className="text-left">Leg</th><th className="text-right">IV</th><th className="text-right">Delta</th><th className="text-right">Gamma</th><th className="text-right">Vega /1pt</th><th className="text-right">Theta /day</th></tr></thead>
        <tbody>{out.map((x, i) => <tr key={i} className="border-t border-[var(--term-border)]"><td>{x.l.label}</td>
          <td className="text-right">{x.iv ? `${(x.iv * 100).toFixed(1)}%` : "no quote"}</td><td className="text-right">{f2(x.g?.delta)}</td>
          <td className="text-right">{x.g ? x.g.gamma.toFixed(4) : "—"}</td><td className="text-right">{f2(x.g?.vega)}</td><td className="text-right">{f2(x.g?.theta)}</td></tr>)}
          <tr className="border-t border-[var(--term-border-strong)] font-bold"><td>Position</td><td /><td className="text-right">{f2(tot("delta"))}</td><td className="text-right">{tot("gamma").toFixed(4)}</td>
            <td className="text-right">{f2(tot("vega"))}</td><td className="text-right">{f2(tot("theta"))}</td></tr></tbody></table></div>
      {out.some((x) => !x.iv) && <div className="text-[10px] text-[#B45309]">A leg without a usable live quote is left out of the position totals.</div>}
    </div>
  );
}

function AnalyseBody({ row, d }) {
  const options = ["OPTIONS", "IRON_CONDOR"].includes(row.category);
  const [tab, setTab] = useState("payoff");
  const tabs = [["payoff", row.category === "FUTURES" ? "P&L vs spread" : row.category === "BOX" ? "Box analysis" : "Payoff chart"],
    ...(row.category !== "BOX" ? [["table", "P&L table"]] : []), ...(options ? [["greeks", "Greeks"]] : [])];
  return (
    <Tabs tabs={tabs} active={tab} onPick={setTab}>
      {tab === "payoff" && <>
        {row.category === "OPTIONS" && <OptionsHedgeAnalysis trade={d.trade} sell={row.strategy === "SELL_OPTIONS"} />}
        {row.category === "IRON_CONDOR" && <IronCondorAnalysis trade={d.trade} />}
        {row.category === "BOX" && <BoxAnalysis id={row.id} />}
        {row.category === "FUTURES" && <FuturesSpreadAnalysis trade={d.trade} />}
      </>}
      {tab === "table" && <PnlTable row={row} d={d} />}
      {tab === "greeks" && <GreeksTable row={row} d={d} />}
      <div className="text-[10px] text-[var(--term-muted)]">The panel's Net P&amp;L ({BASIS[row.pnl_basis] || row.pnl_basis}) is the current figure; expiry payoff is theoretical and only applies at expiry.</div>
    </Tabs>
  );
}

// ───────────────────────── C. TRADE DETAILS ─────────────────────────
function Audit({ row }) {
  const [ev, setEv] = useState(null);
  useEffect(() => {
    const path = { OPTIONS: `/options-hedge/trades/${row.id}/audit`, IRON_CONDOR: `/iron-condor/trades/${row.id}/audit`,
      FUTURES: `/paper/v25/trades/${row.id}/audit` }[row.category];
    api.get(path).then((r) => setEv(r.data.events || r.data.audit || [])).catch(() => setEv([]));
  }, [row]);
  if (!ev) return <div className="text-xs text-[var(--term-muted)]">loading…</div>;
  if (!ev.length) return <NA>No audit events recorded.</NA>;
  return <div className="mono text-[10px] max-h-72 overflow-auto space-y-0.5" data-testid="popup-audit">{ev.map((e, i) => <div key={e.id || i}>
    {when(e.ts || e.at || e.created_at)} · <b>{e.event || e.type}</b> {e.detail ? JSON.stringify(e.detail) : ""}</div>)}</div>;
}

function Orders({ row, d }) {
  const [o, setO] = useState(row.category === "IRON_CONDOR" ? d.trade.orders || [] : null);
  useEffect(() => { if (row.category === "OPTIONS") api.get(`/options-hedge/trades/${row.id}/orders`).then((r) => setO(r.data.orders || [])).catch(() => setO([])); }, [row]);
  if (row.category === "FUTURES") return <NA>Futures orders and fills are in the Trading Book (Orders and Trade Book tabs), filtered to this trade's contracts; ladder fills are under Show Details.</NA>;
  if (!o) return <div className="text-xs text-[var(--term-muted)]">loading…</div>;
  if (!o.length) return <NA>No orders yet.</NA>;
  return (
    <div className="overflow-x-auto max-h-72" data-testid="popup-orders"><table className="mono text-[11px] w-full min-w-[620px]">
      <thead className="text-[var(--term-muted)]"><tr><th className="text-left">Time</th><th className="text-left">Leg / pair</th><th className="text-left">Side</th>
        <th className="text-right">Qty</th><th className="text-right">Price</th><th className="text-left">State</th></tr></thead>
      <tbody>{o.map((x, i) => <tr key={x.id || i} className="border-t border-[var(--term-border)]"><td>{when(x.created_at)}</td>
        <td>{x.leg_id || x.leg || x.pair_no || "—"}{x.tradingsymbol ? ` · ${x.tradingsymbol}` : ""}</td><td>{x.transaction_type || x.side || x.action}</td>
        {/* a SHADOW order is rejected by the broker by design: its fill is the simulated one */}
        <td className="text-right">{x.shadow_simulated_fill ? x.shadow_filled_quantity : (x.filled_quantity ?? x.quantity ?? "—")}</td>
        <td className="text-right">{px(x.shadow_simulated_fill ? x.shadow_fill_price : (x.average_price ?? x.price))}</td>
        <td>{x.state || x.status}{x.shadow_simulated_fill ? " · simulated fill" : ""}</td></tr>)}</tbody></table></div>
  );
}

function DetailsBody({ row, d }) {
  const [tab, setTab] = useState("summary");
  const t = d.trade;
  const tabs = [["summary", "Trade Summary"], ["legs", "Leg Details"], ["economics", "Economics"], ["orders", "Order History"], ["audit", "Audit Log"]];
  return (
    <Tabs tabs={tabs} active={tab} onPick={setTab}>
      {tab === "summary" && <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="popup-summary">
        <KV label="Strategy" value={STRATEGY[row.strategy]?.label} /><KV label="Underlying" value={`${row.market}:${row.underlying}`} />
        <KV label="Name" value={row.name} /><KV label="Environment" value={row.environment} />
        <KV label="Status" value={row.status} /><KV label="Expiry" value={row.expiry || "—"} />
        <KV label="Created" value={when(row.created_at)} /><KV label="Position" value={`${row.position?.text} ${row.position?.unit}`} />
        <KV label="Net P&L" value={signed(row.pnl)} cls={tone(row.pnl)} /><KV label="Basis" value={BASIS[row.pnl_basis] || "—"} />
        <KV label="Target" value={row.target != null ? `₹${px(row.target)}` : row.target_text || "off"} /><KV label="Stop" value={row.stop != null ? `₹${px(row.stop)}` : "off"} />
      </div>}
      {tab === "legs" && <div className="space-y-1 text-xs" data-testid="popup-legs">
        {row.category === "OPTIONS" && (row.strategy === "SELL_OPTIONS"
          ? (d.pnl?.open_contracts || []).map((c, i) => <KV key={i} label={`SHORT ${c.symbol}`} value={`${c.qty} @ ${px(c.entry_avg)}`} />)
          : ["CE", "PE"].map((k) => <KV key={k} label={`BUY ${t[k.toLowerCase()]?.tradingsymbol || k}`} value={`avg ${px(d.pnl?.[`avg_${k.toLowerCase()}`])}`} />))}
        {["IRON_CONDOR", "BOX"].includes(row.category) && (t.legs || []).map((l, i) => {
          const x = (t.fills || {})[`L${l.leg || i + 1}`] || {};
          return <KV key={i} label={`L${l.leg || i + 1} ${l.side} ${l.option_type} ${l.strike}`} value={`in ${x.entry_filled || 0} @ ${px(x.entry_avg)} · out ${x.exit_filled || 0} @ ${px(x.exit_avg)} · open ${x.open || 0}`} />;
        })}
        {row.category === "FUTURES" && <><KV label="SELL" value={t.sell_symbol} cls="text-[#FF3B30]" /><KV label="BUY" value={t.buy_symbol} cls="text-[#2563EB]" />
          <KV label="Avg entry spread" value={px(t.actual_average_entry_spread)} /><KV label="Spread now" value={px(t.current_spread)} /></>}
        {row.category === "OPTIONS" && !d.pnl && <NA>No P&amp;L view for this trade.</NA>}
      </div>}
      {tab === "economics" && <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs" data-testid="popup-economics">
        {row.category === "OPTIONS" && d.pnl && <>
          <KV label="Gross to close" value={signed((d.pnl.unrealized_bid || d.pnl.unrealized_close || {}).gross)} />
          <KV label="Entry charges (open)" value={signed(d.pnl.entry_charges_open)} />
          <KV label="Est. exit charges" value={signed((d.pnl.unrealized_bid || d.pnl.unrealized_close || {}).exit_charges_est)} />
          <KV label="At LTP · indicative" value={signed((d.pnl.unrealized_ltp_indicative || {}).gross)} cls="text-[var(--term-muted)]" />
          {d.pnl.credit_received != null && <KV label="Credit received" value={`₹${px(d.pnl.credit_received)}`} />}
          <KV label="Allot Funds" value={`₹${px(t.allocated_fund)}`} />
        </>}
        {row.category === "IRON_CONDOR" && <>
          <KV label="Plan credit / unit" value={px(t.plan?.credit_unit)} /><KV label="Actual entry / unit" value={px(t.entry_credit_unit)} />
          <KV label="Max loss (plan)" value={t.plan?.metrics?.max_loss_unlimited ? "UNLIMITED" : rupees(t.plan?.metrics?.max_loss)} />
          <KV label="To close · gross" value={signed(t.mtm?.unrealized)} /><KV label="Net liquidation" value={signed(t.mtm?.liquidation_net)} />
          <KV label="Realised (gross)" value={signed(t.realized_pnl)} />
        </>}
        {row.category === "BOX" && <>
          <KV label="Net liquidation" value={signed(t.mtm?.liquidation_net)} /><KV label="Liquidation · gross" value={signed(t.mtm?.liquidation_pnl)} />
          <KV label="Expiry net · theoretical" value={signed(t.confirmed?.net_pnl)} /><KV label="Capital (basket)" value={rupees(t.capital?.capital_required)} />
          <KV label="Realised (gross)" value={signed(t.realized_pnl)} />
        </>}
        {row.category === "FUTURES" && <>
          <KV label="Gross P&L (engine)" value={signed(row.pnl)} cls={tone(row.pnl)} /><KV label="Effective target" value={signed(t.effective_target)} />
          <KV label="Charges incurred" value={signed(t.charges_incurred)} /><KV label="Est. exit charges" value={signed(t.est_remaining_exit_charges)} />
          <KV label="Capital used" value={signed(t.paper_capital_used ?? t.funds_used)} /><KV label="Realised" value={signed(t.realised_pnl)} />
        </>}
      </div>}
      {tab === "orders" && (row.category === "BOX" ? <OrdersAudit id={row.id} /> : <Orders row={row} d={d} />)}
      {tab === "audit" && (row.category === "BOX" ? <OrdersAudit id={row.id} initialTab="audit" /> : <Audit row={row} />)}
    </Tabs>
  );
}

// ───────────────────────── D. SHOW DETAILS ─────────────────────────
function ShowBody({ row, d }) {
  const [tab, setTab] = useState("market");
  const t = d.trade;
  const tabs = [["market", "Market Data"], ["pairs", row.category === "FUTURES" ? "Ladder Levels" : row.category === "OPTIONS" ? "Pair-wise Detail" : "Leg Status"],
    ["execution", "Execution & Reconciliation"], ["alerts", "Alerts & Status"]];
  const quotes = row.category === "OPTIONS" ? Object.entries(d.pnl?.prices?.legs || {}) : [];
  const fr = row.category === "FUTURES" ? t.current_row || {} : null;
  return (
    <Tabs tabs={tabs} active={tab} onPick={setTab}>
      {tab === "market" && <div className="space-y-2 text-xs" data-testid="popup-market">
        <KV label={row.spot_label || "Spot now"} value={row.spot != null ? px(row.spot) : "—"} />
        {quotes.map(([k, q]) => <KV key={k} label={`Leg ${k}`} value={`bid ${px(q?.bid)} · ask ${px(q?.ask)} · ltp ${px(q?.ltp)}${q?.age_s != null ? ` · ${Math.round(q.age_s)}s` : ""} · ${q?.state || "—"}`} />)}
        {fr && <><KV label="Near LTP" value={px(fr.near_ltp)} /><KV label="Far LTP" value={px(fr.far_ltp)} />{fr.spot_ltp != null && <KV label="Spot LTP" value={px(fr.spot_ltp)} />}</>}
        <NA>No intraday price history is stored by GroundZero, so no chart is shown here. Day high / low / change are not reported for trades yet.</NA>
      </div>}
      {tab === "pairs" && <div className="text-xs space-y-1" data-testid="popup-pairs">
        {row.category === "OPTIONS" && ((t.pairs || []).length ? (t.pairs || []).map((p) => <KV key={p.pair_no} label={`Pair ${p.pair_no}`}
          value={`${p.state}${p.protection?.lower != null ? ` · band ${px(p.protection.lower)}–${px(p.protection.upper)}` : ""}${p.exit?.reason ? ` · exit ${p.exit.reason}` : ""}`} />) : <NA>No pairs yet.</NA>)}
        {row.category === "FUTURES" && ((t.levels || []).length ? <div className="overflow-x-auto"><table className="mono text-[11px] w-full min-w-[520px]">
          <thead className="text-[var(--term-muted)]"><tr><th className="text-left">Level</th><th className="text-left">State</th><th className="text-right">Entry spread</th><th className="text-right">Sell / Buy @</th><th className="text-right">P&amp;L</th></tr></thead>
          <tbody>{t.levels.map((l, i) => <tr key={i} className="border-t border-[var(--term-border)]"><td>L{l.level_number ?? i + 1}</td><td>{l.level_status}</td>
            <td className="text-right">{l.sell_entry_price != null && l.buy_entry_price != null ? px(l.sell_entry_price - l.buy_entry_price) : "—"}</td>
            <td className="text-right">{px(l.sell_entry_price)} / {px(l.buy_entry_price)}</td><td className={`text-right ${tone(l.level_pnl)}`}>{signed(l.level_pnl)}</td></tr>)}</tbody></table></div>
          : <NA>No ladder level entered yet.</NA>)}
        {["IRON_CONDOR", "BOX"].includes(row.category) && (t.legs || []).map((l, i) => {
          const x = (t.fills || {})[`L${l.leg || i + 1}`] || {};
          return <KV key={i} label={`L${l.leg || i + 1} ${l.side} ${l.option_type} ${l.strike}`} value={`open ${x.open || 0} · working ${x.working || 0}`} />;
        })}
      </div>}
      {tab === "execution" && <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs" data-testid="popup-execution">
        <KV label="Status" value={row.status} />
        {["IRON_CONDOR", "BOX"].includes(row.category) && <KV label="Reconciliation" value={t.reconciliation ? (t.reconciliation.ok ? "OK" : `MISMATCH · ${(t.reconciliation.problems || []).join("; ")}`) : "—"}
          cls={t.reconciliation?.ok === false ? "text-[#FF3B30]" : ""} />}
        {row.category === "FUTURES" && <><KV label="Reconciliation" value={t.reconciliation_state ?? "n/a (paper)"} /><KV label="Rollover" value={(t.rollover || {}).state || "NONE"} />
          <KV label="Entry gate" value={t.entry_gate?.reason ? String(t.entry_gate.reason).replace(/_/g, " ") : "—"} /></>}
        {row.category === "OPTIONS" && <><KV label="Recovery" value={t.recovery_state || "—"} />
          <KV label="Last engine check" value={t.last_exit_check?.at ? when(t.last_exit_check.at) : "—"} /></>}
        <KV label="Valuation as of" value={row.as_of ? when(row.as_of) : "—"} />
      </div>}
      {tab === "alerts" && <div className="space-y-1 text-xs" data-testid="popup-alerts">
        {[row.needs_attention && "Needs attention (recovery / acknowledgement pending)",
          t.halt_reason && `Halted: ${t.halt_reason}`, t.abort_reason && `Entry aborted: ${t.abort_reason}`, t.exit_reason && `Exit: ${t.exit_reason}`,
          t.exit_in_progress && "Square-off in progress", row.price_state && row.price_state !== "LIVE" && `Prices: ${row.price_state}`]
          .filter(Boolean).map((a, i) => <div key={i} className="rounded-sm border border-[#B45309]/50 bg-[#B45309]/10 text-[#B45309] px-2 py-1 font-semibold">⚠ {a}</div>)}
        <KV label="Auto square-off" value={row.auto?.on ? "ON" : "OFF"} />
      </div>}
    </Tabs>
  );
}

export default function TradePopup({ row, mode, onClose, onChanged = () => {}, onFuturesSettings }) {
  const { d, err, reload } = useDetail(row);
  const s = STRATEGY[row.strategy] || {};
  return (
    <CardDialog open onClose={onClose} wide testId={`popup-${mode}`} title={`${POPUPS[mode]} · ${s.label || ""} ${row.underlying}`}
      sub={`${row.name} · ${row.environment} · ${row.status}`}>
      {err && <div className="text-xs text-[#FF3B30]">{err}</div>}
      {!err && !d && <div className="text-xs text-[var(--term-muted)]">loading…</div>}
      {d && mode === "settings" && <SettingsBody row={row} d={d} reload={reload} onChanged={onChanged} onFuturesSettings={onFuturesSettings} />}
      {d && mode === "analyse" && <AnalyseBody row={row} d={d} />}
      {d && mode === "details" && <DetailsBody row={row} d={d} />}
      {d && mode === "show" && <ShowBody row={row} d={d} />}
    </CardDialog>
  );
}
